/**
 * Where the API lives, decided at page load rather than at build time.
 *
 * The web app and the server are deployed as two applications on two hostnames, so the SPA has to
 * be told the API's origin. Reading it from `/config.js` (rewritten by the container entrypoint)
 * keeps a single built image usable in development, staging and production; leaving it empty falls
 * back to same-origin requests, which is what the Vite dev proxy serves.
 */
declare global {
  interface Window {
    __VOS_BRIDGE_CONFIG__?: { apiBase?: unknown };
  }
}

/** `''` (same origin) or a bare `scheme://host[:port]`. Anything unusable degrades to same-origin. */
export function normalizeApiBase(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return '';
  try {
    return new URL(trimmed).origin;
  } catch {
    return '';
  }
}

export function apiBase(): string {
  return normalizeApiBase(globalThis.window?.__VOS_BRIDGE_CONFIG__?.apiBase);
}

/** Absolute URL for an API path such as `/api/profiles`. */
export function apiUrl(path: string): string {
  return `${apiBase()}${path}`;
}

/** `https://api.example.com` → `wss://api.example.com` */
export function toWsOrigin(httpOrigin: string): string {
  return httpOrigin.replace(/^http/i, 'ws');
}
